import type { PlantSeedStarting } from '@/data/plants';

const METHOD_LABELS: Record<NonNullable<PlantSeedStarting['bestMethod']>, string> = {
  'start-indoors': 'Start indoors',
  'direct-sow': 'Direct sow',
  either: 'Either',
  'transplant-only': 'Transplant only',
};

const STRUCTURED_KEYS: Array<keyof PlantSeedStarting> = [
  'bestMethod',
  'substrate',
  'sowDepth',
  'preSoak',
  'germinationTemp',
  'germinationLight',
  'lightAfterSprouting',
  'lightDistance',
  'moisture',
  'humidityDome',
  'airflow',
  'pottingUp',
  'transplantReadiness',
  'hardeningOff',
  'specialNotes',
];

const LEGACY_NOTES: Array<{
  key: keyof PlantSeedStarting;
  label: string;
  structuredFallbackKeys: Array<keyof PlantSeedStarting>;
}> = [
  { key: 'indoorNotes', label: 'Indoor Start', structuredFallbackKeys: ['bestMethod', 'substrate', 'lightAfterSprouting'] },
  { key: 'directSowNotes', label: 'Direct Sow', structuredFallbackKeys: ['bestMethod', 'sowDepth'] },
  { key: 'germinationNotes', label: 'Germination', structuredFallbackKeys: ['germinationTemp', 'germinationLight'] },
  { key: 'pottingUpNotes', label: 'Potting Up', structuredFallbackKeys: ['pottingUp'] },
  { key: 'hardeningOffNotes', label: 'Hardening Off', structuredFallbackKeys: ['hardeningOff'] },
];

const GROUPS: Array<{
  title: string;
  items: Array<{ key: keyof PlantSeedStarting; label: string }>;
}> = [
  {
    title: 'Setup',
    items: [
      { key: 'substrate', label: 'Substrate' },
      { key: 'preSoak', label: 'Pre-soak' },
      { key: 'germinationLight', label: 'Light to Germinate' },
    ],
  },
  {
    title: 'After Sprouting',
    items: [
      { key: 'lightAfterSprouting', label: 'Grow Light' },
      { key: 'lightDistance', label: 'Light Distance' },
      { key: 'moisture', label: 'Moisture' },
      { key: 'humidityDome', label: 'Humidity Dome' },
      { key: 'airflow', label: 'Airflow' },
    ],
  },
  {
    title: 'Move Up & Out',
    items: [
      { key: 'pottingUp', label: 'Potting Up' },
      { key: 'transplantReadiness', label: 'Ready When' },
    ],
  },
];

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function hasStructuredData(seedStarting: PlantSeedStarting): boolean {
  return STRUCTURED_KEYS.some(key => {
    const value = seedStarting[key];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  });
}

function textFor(seedStarting: PlantSeedStarting, key: keyof PlantSeedStarting): string | null {
  const value = seedStarting[key];
  return hasText(value) ? value.trim() : null;
}

function cleanStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .map(item => item.trim())
    : [];
}

function getSearchableSeedStartingText(seedStarting: PlantSeedStarting): string {
  return Object.values(seedStarting)
    .flatMap(value => {
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string');
      if (value && typeof value === 'object') {
        return Object.values(value).flatMap(nested => {
          if (typeof nested === 'string') return nested;
          if (Array.isArray(nested)) return nested.filter((item): item is string => typeof item === 'string');
          return [];
        });
      }
      return [];
    })
    .join(' ')
    .toLowerCase();
}

function isEstablishmentGuidance(seedStarting: PlantSeedStarting): boolean {
  if (seedStarting.bestMethod === 'transplant-only') return true;

  const searchableText = getSearchableSeedStartingText(seedStarting);
  return [
    'clove',
    'crown',
    'slip',
    'tuber',
    'division',
    'rooted cutting',
    'root cutting',
    'nursery',
    'bare-root',
  ].some(term => searchableText.includes(term));
}

function getHardeningOff(seedStarting: PlantSeedStarting):
  | { kind: 'legacy'; text: string }
  | { kind: 'structured'; duration: string | null; steps: string[]; warnings: string[] }
  | null {
  const value = (seedStarting as { hardeningOff?: unknown }).hardeningOff;
  if (hasText(value)) return { kind: 'legacy', text: value.trim() };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const protocol = value as {
    duration?: unknown;
    steps?: unknown;
    criticalWarnings?: unknown;
  };
  const duration = hasText(protocol.duration) ? protocol.duration.trim() : null;
  const steps = cleanStringList(protocol.steps);
  const warnings = cleanStringList(protocol.criticalWarnings);

  if (!duration && steps.length === 0 && warnings.length === 0) return null;
  return { kind: 'structured', duration, steps, warnings };
}

function DetailRow({ label, text }: { label: string; text: string }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-1 sm:gap-3">
      <dt className="font-body text-[12px] text-sage font-semibold">
        {label}
      </dt>
      <dd className="font-body text-[14px] text-dark-text leading-snug">
        {text}
      </dd>
    </div>
  );
}

export default function PlantSeedStartingNotes({
  seedStarting,
}: {
  seedStarting?: PlantSeedStarting;
}) {
  if (!seedStarting) return null;

  const hasStructured = hasStructuredData(seedStarting);
  const hardeningOff = getHardeningOff(seedStarting);
  const isEstablishment = isEstablishmentGuidance(seedStarting);
  const sectionTitle = isEstablishment ? 'Planting & Establishment Notes' : 'Seed Starting Notes';
  const depthLabel = isEstablishment ? 'Planting Depth' : 'Sow Depth';
  const bestMethod = seedStarting.bestMethod ? METHOD_LABELS[seedStarting.bestMethod] : null;
  const topItems = [
    bestMethod ? { label: 'Best Method', text: bestMethod } : null,
    textFor(seedStarting, 'sowDepth') ? { label: depthLabel, text: textFor(seedStarting, 'sowDepth') as string } : null,
    textFor(seedStarting, 'germinationTemp') ? { label: 'Germination Temp', text: textFor(seedStarting, 'germinationTemp') as string } : null,
  ].filter((item): item is { label: string; text: string } => Boolean(item));

  const groups = GROUPS
    .map(group => ({
      ...group,
      items: group.items
        .map(item => ({ ...item, text: textFor(seedStarting, item.key) }))
        .filter((item): item is typeof item & { text: string } => Boolean(item.text)),
    }))
    .filter(group => group.items.length > 0);

  const specialNotes = Array.isArray(seedStarting.specialNotes)
    ? seedStarting.specialNotes.map(note => note.trim()).filter(Boolean)
    : [];

  const legacyNotes = LEGACY_NOTES
    .filter(note => {
      if (!hasText(seedStarting[note.key])) return false;
      if (!hasStructured) return true;
      return !note.structuredFallbackKeys.some(key => {
        const value = seedStarting[key];
        return Array.isArray(value) ? value.length > 0 : Boolean(value);
      });
    })
    .map(note => ({ label: note.label, text: textFor(seedStarting, note.key) as string }));

  if (
    topItems.length === 0 &&
    groups.length === 0 &&
    specialNotes.length === 0 &&
    !hardeningOff &&
    legacyNotes.length === 0
  ) {
    return null;
  }

  return (
    <div className="pt-4 mt-4 border-t border-sand">
      <p className="font-body font-semibold text-[12px] text-sage uppercase tracking-wider">
        {sectionTitle}
      </p>

      {topItems.length > 0 && (
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {topItems.map(item => (
            <div key={item.label}>
              <dt className="font-body text-[11px] text-sage uppercase tracking-wider font-semibold">
                {item.label}
              </dt>
              <dd className="font-body text-[14px] text-dark-text mt-0.5 leading-snug">
                {item.text}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {groups.length > 0 && (
        <div className="mt-4 space-y-4">
          {groups.map(group => (
            <section key={group.title} className="space-y-2">
              <h3 className="font-body text-[12px] text-sage font-semibold">
                {group.title}
              </h3>
              <dl className="space-y-2">
                {group.items.map(item => (
                  <DetailRow key={item.key} label={item.label} text={item.text} />
                ))}
              </dl>
            </section>
          ))}
        </div>
      )}

      {hardeningOff && (
        <section className="mt-4 space-y-2">
          <h3 className="font-body text-[12px] text-sage font-semibold">
            Hardening Off
          </h3>
          {hardeningOff.kind === 'legacy' ? (
            <dl>
              <DetailRow label="Hardening Off" text={hardeningOff.text} />
            </dl>
          ) : (
            <div className="space-y-2.5">
              {hardeningOff.duration && (
                <dl>
                  <DetailRow label="Duration" text={hardeningOff.duration} />
                </dl>
              )}
              {hardeningOff.steps.length > 0 && (
                <ol className="space-y-1.5 list-decimal pl-5">
                  {hardeningOff.steps.slice(0, 3).map(step => (
                    <li key={step} className="font-body text-[14px] text-dark-text leading-snug">
                      {step}
                    </li>
                  ))}
                </ol>
              )}
              {hardeningOff.warnings.length > 0 && (
                <ul className="space-y-1.5">
                  {hardeningOff.warnings.slice(0, 2).map(warning => (
                    <li key={warning} className="font-body text-[13px] text-warm-brown leading-snug">
                      {warning}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      {specialNotes.length > 0 && (
        <div className="mt-4 space-y-2">
          <h3 className="font-body text-[12px] text-sage font-semibold">
            Special Notes
          </h3>
          <ul className="space-y-1.5">
            {specialNotes.map(note => (
              <li key={note} className="font-body text-[14px] text-dark-text leading-snug">
                {note}
              </li>
            ))}
          </ul>
        </div>
      )}

      {legacyNotes.length > 0 && (
        <dl className="mt-3 space-y-2.5">
          {legacyNotes.map(note => (
            <DetailRow key={note.label} label={note.label} text={note.text} />
          ))}
        </dl>
      )}
    </div>
  );
}
